import { z } from 'zod';
import validator from 'validator';
import ISO6391 from 'iso-639-1';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { groupRelationships } from './capabilities.js';
import { capabilitiesSchema } from './config.js';
import { HANDLE_RESERVED_MESSAGE, additionalForbiddenHandlesSchema, classifyAdditionalForbiddenHandle, collectAdditionalForbiddenHandles, forbiddenHandles, handleRegex, handleRegexBase, isDefaultForbiddenHandle, isForbiddenHandle, normalizeForbiddenHandle, } from './forbiddenHandles.js';
export { HANDLE_RESERVED_MESSAGE, additionalForbiddenHandlesSchema, classifyAdditionalForbiddenHandle, collectAdditionalForbiddenHandles, forbiddenHandles, handleRegex, handleRegexBase, isDefaultForbiddenHandle, isForbiddenHandle, normalizeForbiddenHandle, };
extendZodWithOpenApi(z);
/** `@handle` using the allowed handle charset. No `\\b` — hyphens are not word chars. */
export const mentionHandleRegexBase = `(^|[\\s(>])@(${handleRegexBase})(?=\\s|$|[.,!?;:])`;
export const baseSchemaShape = {
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    deletedAt: z.iso.datetime().nullable().optional(),
};
export const baseSchema = z.object(baseSchemaShape);
export const idSchemaShape = {
    id: z.uuid(),
};
export const idSchema = z.object(idSchemaShape);
const modelBaseFields = {
    ...baseSchemaShape,
    ...idSchemaShape,
};
/**
 * Preserves the data-schema shape through declaration emit (see ZodObject extend typing).
 * A non-generic parameter used to widen `ZodObject<ZodRawShape>` destroys output `.d.ts`
 * (index signature + unknown), which breaks consumers that need structural JSON types.
 */
export const modelSchema = (dataSchema) => dataSchema.extend(modelBaseFields);
const connectionSchema = (dataSchema, from, to) => modelSchema(dataSchema).extend({
    from,
    to,
});
export const applicationDataSchema = z.object({
    id: z.string(),
    secret: z.string(),
    name: z.string(),
    website: z.string(),
});
export const accountDataSchema = z.object({
    email: z.string().email(),
    passwordHash: z.string(),
    emailValidated: z.boolean().default(false).optional(),
    guest: z.boolean().optional(),
});
export const accountSchema = modelSchema(accountDataSchema);
export const roleDataSchema = z.object({
    key: z.string().regex(/^[a-z-]{1,32}$/),
    default: z.boolean(),
    /** Defaults history version the role's capabilities were based on. */
    baseVersion: z.string().optional(),
    displayName: z.string().max(32),
    description: z.string().optional(),
    capabilities: capabilitiesSchema,
});
export const roleSchema = modelSchema(roleDataSchema);
export const profileSettingsDefaultsSchema = z.object({
    privacy: z.enum(['public', 'unlisted', 'private']).optional(),
    sensitive: z.boolean().optional(),
    language: z
        .string()
        .refine(validator.isISO6391)
        .optional()
        .openapi({ type: 'string', description: 'ISO-639-1 language code' }),
});
export const profileNotificationSettings = z.object({
    create: z.boolean(),
    push: z.boolean(),
    email: z.boolean(),
});
/** Per-group activity level. Unset means `all`, matching account settings. */
export const groupNotificationLevelOptions = [
    'all',
    'mostRelevant',
    'off',
];
export const groupNotificationLevelSchema = z.enum(groupNotificationLevelOptions);
export const jamSettingsSchema = z.object({
    backgroundObfuscation: z
        .discriminatedUnion('type', [
        z.object({
            type: z.literal('blur'),
            radius: z.number().int().positive().optional(),
        }),
        z.object({
            type: z.literal('image'),
            image: z.string().url(),
        }),
        z.object({
            type: z.literal('video'),
            video: z.string().url(),
        }),
        z.object({
            type: z.literal('none'),
        }),
    ])
        .optional(),
});
export const THEME_OPTIONS = ['community', 'light', 'dark', 'system'];
export const ThemeOptionsSchema = z.enum(THEME_OPTIONS);
export const profileSettingsDataSchema = z.object({
    id: z.string(),
    language: z.string().optional(),
    timeZone: z.string().optional(),
    theme: ThemeOptionsSchema.optional(),
    defaults: profileSettingsDefaultsSchema.optional(),
    notifications: z.record(z.string(), profileNotificationSettings).optional(),
    /** Per-group level. A missing group id means all activity for that group. */
    groupNotificationLevels: z
        .record(z.string(), groupNotificationLevelSchema)
        .optional(),
    jamSettings: jamSettingsSchema.optional(),
    stripeSettings: z
        .object({
        customerId: z.string().optional(),
    })
        .optional(),
    feedSettings: z
        .object({
        format: z.enum(['threaded', 'linear']).optional(),
        communityFeed: z
            .object({
            showGroupPosts: z.boolean().optional(),
        })
            .optional(),
    })
        .default({ communityFeed: { showGroupPosts: true } })
        .optional(),
});
export const profileSettingsSchema = modelSchema(profileSettingsDataSchema);
export const accountNameSchema = z
    .string()
    .refine((value) => !isForbiddenHandle(value), {
    message: HANDLE_RESERVED_MESSAGE,
})
    .refine((value) => handleRegex.test(value ?? ''), {
    message: 'Handle must be 1–16 characters: letters, numbers, underscores, or hyphens (no spaces or punctuation)',
})
    .openapi({
    type: 'string',
    pattern: `^${handleRegexBase}$`,
    description: 'Account handle: 1-16 alphanumeric / underscore / hyphen characters, excluding a reserved set.',
});
const profileResourceTypes = [
    'jams',
    'posts',
    'self',
    'profiles',
    'posts',
    'groups',
    'reports',
    'webhooks',
    'notifications',
];
export const profileResourceTypeSchema = z.enum(profileResourceTypes);
const serviceResourceTypes = [
    'analytics',
    'render', // Deprecated
    'webhooks',
    'posts',
    'profiles',
    'db',
];
export const serviceResourceTypeSchema = z.enum(serviceResourceTypes);
const resourceTypeSchema = z.enum([
    ...profileResourceTypes,
    ...serviceResourceTypes,
    '*',
]);
export const resourceSchema = z.object({
    type: resourceTypeSchema,
    id: z.union([z.string(), z.literal('*')]).optional(),
});
export const scopeLevelSchema = z.enum(['write', 'read', 'admin']);
export const scopeSchema = z.object({
    scopeLevel: scopeLevelSchema.optional(),
    resource: resourceSchema,
});
export const accessTokenDataSchema = z.object({
    name: z.string(),
    description: z.string().optional(),
    signedToken: z.string(),
    revokedAt: z.iso.datetime().nullable().optional(),
    expiresAt: z.iso.datetime().nullable().optional(),
});
export const accessTokenSchema = modelSchema(accessTokenDataSchema);
export const latLngSchema = z.object({
    lat: z.number(),
    lng: z.number(),
    alt: z.number().optional(),
});
export const locationSchema = z.object({
    text: z.string(),
    coordinates: latLngSchema.optional(),
});
export const federatedHandleSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-zA-Z0-9_.-]+$/, {
    message: 'Federated handle: 1–64 letters, numbers, underscores, dots, or hyphens',
});
export const profileDataSchema = z.object({
    handle: federatedHandleSchema,
    deletedHandle: accountNameSchema.optional(),
    avatar: z.url().nullable().optional(),
    header: z.url().nullable().optional(),
    displayName: z.string().max(30).optional(),
    bio: z.string().optional(),
    timeZone: z.string().optional(),
    location: locationSchema.optional(),
    locked: z.boolean().optional(),
    bot: z.boolean().optional(),
    discoverable: z.boolean().optional(),
    hideSocialGraph: z.boolean().optional(),
    indexable: z.boolean().optional(),
    fields: z
        .array(z.object({
        name: z.string().max(255).min(1),
        value: z.string().max(255),
    }))
        .optional(),
    activityPub: z
        .object({
        domain: z.string().min(4), // TODO maybe replace with regex
    })
        .optional(),
    type: z.enum(['local', 'guest', 'federated']),
    guestData: z
        .object({
        email: z.email().optional(),
        resource: resourceSchema.optional(),
    })
        .optional(),
    pinnedPostId: z.string().optional(),
});
export const profileSchema = modelSchema(profileDataSchema);
export const accountWithMetaSchema = accountSchema.extend({
    profiles: z.array(profileSchema),
});
export const jamDataSchema = z
    .object({
    moderators: z.string().array(),
    speakers: z.string().array().optional(),
    presenters: z.string().array().optional(),
    audience: z.string().array().optional(),
    videoEnabled: z.boolean(),
    type: z.enum(['video-call']),
    maxSpeakers: z.number().int().positive().optional(),
    maxPresenters: z.number().int().positive().optional(),
    maxAudience: z.number().int().positive().optional(),
    waitingRoom: z.boolean().optional(),
})
    .openapi('Jam');
export const jamSchema = jamDataSchema;
export const followDataSchema = z.object({
    /** ActivityPub Follow activity id when federated. */
    uri: z.string().url().optional(),
    reblogs: z.boolean().optional(),
    notify: z.boolean().optional(),
    languages: z
        .array(z
        .string()
        .refine(validator.isISO6391)
        .openapi({ type: 'string', description: 'ISO-639-1 language code' }))
        .optional(),
});
export const followSchema = connectionSchema(followDataSchema, profileSchema, profileSchema);
export const visibilityTypeValues = [
    'public',
    'unlisted',
    'private',
    'direct',
    'local',
    'group',
    'report',
];
export const visibilityTypeSchema = z.enum(visibilityTypeValues);
export const postTypeSchema = z.enum([
    'note',
    'question',
    'event',
    'article',
    'resource',
]);
export const resourceKindSchema = z.enum([
    'video',
    'file',
    'gallery',
    'audio',
    'link',
]);
export const mediaAttachmentStatusSchema = z.enum([
    'processing',
    'ready',
    'failed',
]);
export const mediaAttachmentDataSchema = z
    .object({
    type: z.string(),
    url: z.url(),
    previewUrl: z.url().nullable(),
    textUrl: z.null(),
    filename: z.string(),
    meta: z.object({
        usage: z.string().optional(),
        focus: z
            .object({
            x: z.number(),
            y: z.number(),
        })
            .optional(),
        mimetype: z.string().optional(),
        size: z.number().optional(),
    }),
    description: z.string().optional(),
    blurhash: z.string().optional(),
    status: mediaAttachmentStatusSchema.optional(),
    error: z.string().optional(),
})
    .openapi('MediaAttachment');
export const mediaAttachmentSchema = modelSchema(mediaAttachmentDataSchema);
export const processingStatsDataSchema = z
    .object({
    mediaAttachmentId: z.string().uuid(),
    filesize: z.number(),
    filetype: z.string(),
    durationMs: z.number(),
})
    .openapi('ProcessingStats');
export const processingStatsSchema = modelSchema(processingStatsDataSchema);
export const customEmojiSchema = z
    .object({
    shortcode: z.string(),
    url: z.url(),
    staticUrl: z.url(),
    visibleInPicker: z.boolean(),
    category: z.string(),
})
    .openapi('CustomEmoji');
export const noteSchema = z
    .object({
    type: z.literal('note'),
    content: z.string().optional(),
    sensitive: z.boolean().optional(),
    spoilerText: z.string().optional(),
    attachments: z.array(mediaAttachmentDataSchema).optional(),
    customEmojis: z.array(customEmojiSchema).optional(),
    language: z.enum(ISO6391.getAllCodes()).optional(),
})
    .openapi('Note');
/** Poll choices are stored short so they fit the vote UI on small screens. */
export const POLL_OPTION_MAX_LENGTH = 30;
export const questionSchema = noteSchema
    .extend({
    type: z.literal('question'),
    options: z
        .object({
        content: z.string().max(POLL_OPTION_MAX_LENGTH),
        type: z.literal('note'),
    })
        .array(),
    multiple: z.boolean().optional(),
    votersVisible: z.boolean().optional(),
    expiresAt: z.iso.datetime().optional(),
})
    .openapi('Question');
export const articleSchema = noteSchema
    .extend({
    type: z.literal('article'),
    image: z.url().optional(),
    title: z.string().optional(),
})
    .openapi('Article');
/** Resource titles are library headings, so they stay much shorter than the description. */
export const RESOURCE_TITLE_MAX_LENGTH = 200;
export const resourcePostSchema = noteSchema
    .extend({
    type: z.literal('resource'),
    title: z.string().max(RESOURCE_TITLE_MAX_LENGTH),
    tags: z.array(z.string().min(1)).optional(),
    categoryPath: z.array(z.string().min(1)).optional(),
    resourceKind: resourceKindSchema,
    url: z.url().optional(),
})
    .openapi('ResourcePost');
export const eventIsoDatetimeSchema = z.iso.datetime({
    offset: true,
    precision: 3,
});
export const recurrenceFreqSchema = z.enum(['DAILY', 'WEEKLY', 'MONTHLY']);
export const recurrenceWeekdaySchema = z.enum([
    'MO',
    'TU',
    'WE',
    'TH',
    'FR',
    'SA',
    'SU',
]);
export const eventRecurrenceSchema = z
    .object({
    freq: recurrenceFreqSchema,
    interval: z.number().int().positive().optional(),
    byDay: recurrenceWeekdaySchema.array().optional(),
    until: eventIsoDatetimeSchema.optional(),
    count: z.number().int().positive().optional(),
})
    .refine((value) => !(value.until && value.count), {
    message: 'until and count are mutually exclusive',
    path: ['until'],
})
    .openapi('EventRecurrence');
export const eventOccurrenceExceptionSchema = z
    .object({
    recurrenceId: eventIsoDatetimeSchema,
    cancelled: z.boolean().optional(),
    start: eventIsoDatetimeSchema.optional(),
    end: eventIsoDatetimeSchema.optional(),
    physicalLocation: locationSchema.optional(),
})
    .openapi('EventOccurrenceException');
export const eventSchema = noteSchema
    .extend({
    type: z.literal('event'),
    name: z.string().optional(),
    timeZone: z.string().optional(),
    image: z.url().optional(),
    start: eventIsoDatetimeSchema,
    end: eventIsoDatetimeSchema.optional(),
    wholeDay: z.boolean(),
    maxAttendees: z.number().int().positive().optional(),
    /** Minutes the next waitlisted member has to claim a freed seat. */
    waitlistOfferMinutes: z.number().int().positive().max(10080).optional(),
    attendeeListPublic: z.boolean().optional(),
    moderators: z.string().uuid().array().optional(),
    jam: jamSchema.optional(),
    physicalLocation: locationSchema.optional(),
    url: z.url().optional(),
    recurrence: eventRecurrenceSchema.optional(),
    exceptions: eventOccurrenceExceptionSchema.array().optional(),
})
    .refine((data) => !data.end || new Date(data.end) > new Date(data.start), {
    message: 'events.form.endBeforeStart',
    path: ['end'],
})
    .openapi('Event');
export const postDataUnionSchema = z.discriminatedUnion('type', [
    noteSchema,
    questionSchema,
    eventSchema,
    articleSchema,
    resourcePostSchema,
]);
export const postDataSchema = z.object({
    type: postTypeSchema,
    visibility: visibilityTypeSchema,
    capabilitiesNeeded: z.string().array().optional(),
    data: postDataUnionSchema.optional(),
    creatorId: z.string(),
});
export const postSchema = modelSchema(postDataSchema);
export const answerSchema = z
    .object({
    selection: z.number().array(),
})
    .openapi('Answer');
export const rsvpResponseSchema = z.enum([
    'yes',
    'no',
    'tentative',
    'removed',
    'waitlist',
]);
export const rsvpSchema = z.object({
    response: rsvpResponseSchema,
    recurrenceId: eventIsoDatetimeSchema.optional(),
    /** Queue position. Copied onto later offer entries so a new write stays in line. */
    waitlistJoinedAt: eventIsoDatetimeSchema.optional(),
    /** When the current spot offer expires. Server-set; ignored on RSVP requests. */
    offerExpiresAt: eventIsoDatetimeSchema.optional(),
});
export const rsvpRequestSchema = rsvpSchema
    .omit({ waitlistJoinedAt: true, offerExpiresAt: true })
    .extend({
    recurrenceIds: z.array(eventIsoDatetimeSchema).min(1).optional(),
});
export const entryReactionDataSchema = z.object({
    reaction: z.string().min(1),
});
export const entryDataSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('create'),
        data: postDataUnionSchema,
    }),
    z.object({
        type: z.literal('edit'),
        data: postDataUnionSchema,
    }),
    z.object({
        type: z.literal('delete'),
        data: z.any(),
    }),
    z.object({
        type: z.literal('answer'),
        data: answerSchema,
    }),
    z.object({
        type: z.literal('rsvp'),
        data: rsvpSchema,
    }),
    z.object({
        type: z.literal('reaction'),
        data: entryReactionDataSchema,
        uri: z.string().url().optional(),
    }),
    z.object({
        type: z.literal('unreaction'),
        data: entryReactionDataSchema,
        uri: z.string().url().optional(),
    }),
]);
export const accountInteractionsSchema = z.object({
    favorited: z.boolean(),
    reposted: z.boolean(),
    muted: z.boolean(),
    bookmarked: z.boolean(),
    pinned: z.boolean(),
});
export const reportResolutionSchema = z.enum([
    'ignore',
    'remove',
    'warn',
    'ban',
    'other',
]);
export const reportDataSchema = z
    .object({
    comment: z.string().max(1000),
    uri: z.string().url().optional(),
    forward: z.boolean().optional(),
    category: z.enum(['spam', 'violation', 'other']),
    ruleIds: z.array(z.number()).optional(),
    resolution: reportResolutionSchema.optional(),
})
    .openapi('Report');
export const reportSchema = modelSchema(reportDataSchema);
export const notificationDataSchema = z.object({
    type: z.string(),
    profileId: z.string().uuid(),
    pushHandled: z.boolean().optional(),
    emailHandled: z.boolean().optional(),
    read: z.boolean().optional(),
    seen: z.boolean().optional(),
    postId: z.string().optional(),
    fromProfileId: z.string().uuid().optional(),
    groupId: z.string().uuid().optional(),
    data: z.unknown().optional(),
});
export const notificationSchema = modelSchema(notificationDataSchema);
export const configDataSchema = z.object({
    config: z.any(),
});
export const configDataWithDefaultsSchema = configDataSchema.extend({
    defaults: z.any(),
});
export const alertsSchema = z.object({
    poll: z.boolean(),
    followRequest: z.boolean(),
    status: z.boolean(),
    favorite: z.boolean(),
    follow: z.boolean(),
    repost: z.boolean(),
    mention: z.boolean(),
    update: z.boolean(),
    admin: z.object({
        signUp: z.boolean(),
        report: z.boolean(),
    }),
});
const pushSubscriptionWebDataSchema = z.object({
    type: z.literal('web'),
    deviceName: z.string().optional(),
    endpoint: z.string().url(),
    keys: z.object({
        auth: z.string(),
        p256dh: z.string(),
    }),
    alerts: alertsSchema.optional(),
});
const pushSubscriptionApnDataSchema = z.object({
    type: z.literal('apn'),
    deviceName: z.string().optional(),
    apnToken: z.string(),
    alerts: alertsSchema.optional(),
});
const pushSubscriptionFcmDataSchema = z.object({
    type: z.literal('fcm'),
    deviceName: z.string().optional(),
    fcmToken: z.string(),
    alerts: alertsSchema.optional(),
});
const pushSubscriptionWebhookDataSchema = z.object({
    type: z.literal('webhook'),
    url: z.url(),
    publicKey: z.string().min(1),
    alerts: alertsSchema.optional(),
});
export const pushSubscriptionDataSchema = z.discriminatedUnion('type', [
    pushSubscriptionWebDataSchema,
    pushSubscriptionApnDataSchema,
    pushSubscriptionFcmDataSchema,
    pushSubscriptionWebhookDataSchema,
]);
export const pushSubscriptionSchema = z.discriminatedUnion('type', [
    modelSchema(pushSubscriptionWebDataSchema),
    modelSchema(pushSubscriptionApnDataSchema),
    modelSchema(pushSubscriptionFcmDataSchema),
    modelSchema(pushSubscriptionWebhookDataSchema),
]);
export const reactionTypeSchema = z.enum(['👍']);
export const reactionDataSchema = z.object({
    reaction: reactionTypeSchema,
});
export const mentionDataSchema = z.object({
    text: z.string().optional(),
});
export const timelineValueSchema = z.object({
    start: z.string().datetime(),
    end: z.string().datetime(),
    value: z.number(),
});
export const profileStatsSchema = z.object({
    followersCount: z.number(),
    followingCount: z.number(),
});
export const GROUP_DISPLAY_NAME_MAX_LENGTH = 30;
export const groupDataSchema = z.object({
    handle: accountNameSchema,
    avatar: z.string().url().nullable().optional(),
    header: z.string().url().nullable().optional(),
    displayName: z
        .string()
        .max(GROUP_DISPLAY_NAME_MAX_LENGTH, {
        message: `Group name should be ${GROUP_DISPLAY_NAME_MAX_LENGTH} characters or fewer`,
    })
        .optional(),
    description: z.string().optional(),
    rules: z.string().optional(),
    pinnedPostId: z.string().optional(),
    discoverable: z.boolean().optional(),
    capabilities: z.record(z.string(), capabilitiesSchema),
});
export const groupSchema = modelSchema(groupDataSchema);
export const groupRoleSchema = z.object({
    roles: z.enum(groupRelationships).array().optional().nullable(),
});
export const guestDataSchema = z.object({
    displayName: z.string(),
    email: z.email(),
});
export const jamEventDataSchema = z.object({
    id: z.string(),
    jamId: z.string(),
    type: z.enum([
        'message',
        'reaction',
        'join',
        'leave',
        'start',
        'close',
        'recordStart',
        'recordStop',
        'streamStart',
        'streamStop',
    ]),
    profileId: z.string(),
    content: z.string().optional(),
    reaction: reactionTypeSchema.optional(),
    recurrenceId: eventIsoDatetimeSchema.optional(),
});
export const jamEventSchema = modelSchema(jamEventDataSchema);
export const inviteLinkDataSchema = z.object({
    slug: z.string(),
    active: z.boolean(),
    emailPatterns: z.string().array().optional(),
    maxUses: z.number(),
    expiresAt: z.string().datetime(),
    customInvitationMessage: z.string().optional(),
    /** New members who redeem this link are added to these groups (as members). */
    groupIds: z.array(z.string()).optional(),
});
export const inviteLinkSchema = modelSchema(inviteLinkDataSchema);
export const hashtagDataSchema = z.object({
    tag: z.string().optional(),
});
export const hashtagSchema = modelSchema(hashtagDataSchema);
export const normalizeHashtagTag = (tag) => tag.toLowerCase();
export const hashtagRegexBase = '(?<!\\S)#([a-z0-9_]+)(?![a-z0-9_])';
export const hashtagRegex = new RegExp(hashtagRegexBase, 'gi');
export const jamRecordingTypeSchema = z.enum([
    'compositeVideo',
    'compositeAudio',
    'streams',
]);
export const jamRecordingKindSchema = z.enum(['file', 'rtmp']);
export const jamRecordingStatusSchema = z.enum([
    'requested',
    'active',
    'finalizing',
    'completed',
    'failed',
]);
export const jamRecordingDataSchema = z.object({
    status: jamRecordingStatusSchema,
    kind: jamRecordingKindSchema.optional(),
    destinationHost: z.string().optional(),
    egressId: z.string().optional(),
    attachment: mediaAttachmentSchema.optional(),
    replyPostId: z.string().optional(),
});
export const jamRecordingSchema = modelSchema(jamRecordingDataSchema);
